import { useCallback, useEffect, useMemo, useState } from "react";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import showErrorToast from "../../components/CustomComponents/toast/toast.js";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import Select from "react-select";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";

const defaultForm = {
  shiftSlot_ID: 0,
  countryCode: "PAK",
  serviceCity: "",
  zoneId: null,
  shiftDateFrom: "",
  shiftDateTo: "",
  startTime: "09:00",
  endTime: "17:00",
  maxRiders: 10,
  isActive: true,
  notes: "",
};

const AddUpdateShiftSlotModal = ({ isOpen, onClose, onSubmit, slot = null }) => {
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [form, setForm] = useState(defaultForm);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const isEdit = !!(slot && (slot.shiftSlotId || slot.ShiftSlot_ID));

  const fieldStyle = useMemo(
    () => ({
      borderRadius: "10px",
      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
      background: isDarkMode ? "#2d3436" : "#F9F9F9",
      color: isDarkMode ? "#ffffff" : "#495057",
    }),
    [isDarkMode]
  );

  const zoneSelectStyles = useMemo(
    () => ({
      ...dropdownStyles,
      control: (provided, state) => ({
        ...dropdownStyles.control(provided, state),
        minHeight: "44px",
        height: "44px",
        borderRadius: "10px",
        background: isDarkMode ? "#2d3436" : "#F9F9F9",
        border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
        boxShadow: "none",
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 11000,
      }),
      menu: (provided) => ({
        ...dropdownStyles.menu(provided),
        zIndex: 11000,
      }),
    }),
    [dropdownStyles, isDarkMode]
  );

  const loadZones = useCallback(async (countryCode, serviceCity) => {
    if (!countryCode || !serviceCity) {
      setZoneOptions([]);
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_ZONES, {
        params: {
          CountryCode: countryCode,
          ServiceCity: serviceCity,
          IsActive: true,
          PageNumber: 1,
          PageSize: 200,
        },
      });
      if (response.data?.status) {
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        setZoneOptions(
          rows.map((z) => ({
            value: z.zoneId || z.Zone_ID,
            label: z.zoneName || z.ZoneName,
          }))
        );
      } else {
        setZoneOptions([]);
      }
    } catch {
      setZoneOptions([]);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (slot) {
      const dateVal = slot.shiftDate || slot.ShiftDate;
      const dateStr = dateVal ? String(dateVal).substring(0, 10) : "";
      const country = slot.countryCode || slot.CountryCode || "PAK";
      const city = slot.serviceCity || slot.ServiceCity || "";
      const zoneId = slot.zoneId || slot.Zone_ID || null;
      const zoneName = slot.zoneName || slot.ZoneName || "";
      setForm({
        shiftSlot_ID: slot.shiftSlotId || slot.ShiftSlot_ID || 0,
        countryCode: country,
        serviceCity: city,
        zoneId,
        shiftDateFrom: dateStr,
        shiftDateTo: dateStr,
        startTime: (slot.startTime || slot.StartTime || "09:00").toString().substring(0, 5),
        endTime: (slot.endTime || slot.EndTime || "17:00").toString().substring(0, 5),
        maxRiders: slot.maxRiders ?? slot.MaxRiders ?? 10,
        isActive: slot.isActive ?? slot.IsActive ?? true,
        notes: slot.notes || slot.Notes || "",
      });
      if (zoneId) setSelectedZone({ value: zoneId, label: zoneName || `Zone ${zoneId}` });
      else setSelectedZone(null);
      loadZones(country, city);
    } else {
      setForm(defaultForm);
      setSelectedZone(null);
      setZoneOptions([]);
    }
  }, [isOpen, slot, loadZones]);

  useEffect(() => {
    if (!isOpen) return;
    loadZones(form.countryCode, form.serviceCity);
  }, [isOpen, form.countryCode, form.serviceCity, loadZones]);

  useEffect(() => {
    if (!form.zoneId || zoneOptions.length === 0) return;
    const match = zoneOptions.find((o) => o.value === form.zoneId);
    if (match) setSelectedZone(match);
  }, [zoneOptions, form.zoneId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = () => {
    if (!form.zoneId || !form.shiftDateFrom || !form.startTime || !form.endTime) {
      showErrorToast("Zone, date, and times are required");
      return;
    }
    const toDate = isEdit ? form.shiftDateFrom : (form.shiftDateTo || form.shiftDateFrom);
    if (!isEdit && toDate && form.shiftDateFrom && toDate < form.shiftDateFrom) {
      showErrorToast("End date must be on or after start date");
      return;
    }
    onSubmit({
      shiftSlot_ID: form.shiftSlot_ID,
      zone_ID: form.zoneId,
      serviceCity: form.serviceCity.trim(),
      shiftDate: form.shiftDateFrom,
      shiftDateEnd: isEdit ? form.shiftDateFrom : toDate,
      startTime: form.startTime,
      endTime: form.endTime,
      maxRiders: Number(form.maxRiders) || 10,
      isActive: form.isActive,
      notes: form.notes.trim() || null,
    });
  };

  return (
    <CustomModal
      title={isEdit ? "Edit shift slot" : "Add shift slots"}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSave}
      showButton={true}
      submitButtonText={isEdit ? "Save" : "Create slots"}
      isCentered
      modalSize="modal-lg"
      maxHeight="90vh"
      minHeight="320px"
      zIndex={10050}
    >
      <div className="row g-3">
        <CountryCitySelector
          selectedCountry={form.countryCode}
          selectedCity={form.serviceCity}
          setSelectedCity={(city) =>
            setForm((prev) => ({ ...prev, serviceCity: city, zoneId: null }))
          }
          onChange={(payload) => {
            setForm((prev) => ({
              ...prev,
              countryCode: payload.CompCountry !== undefined ? (payload.CompCountry || "PAK") : prev.countryCode,
              serviceCity: payload.CompCity !== undefined ? (payload.CompCity || "") : prev.serviceCity,
              zoneId: null,
            }));
            setSelectedZone(null);
          }}
          selectedValueIsLabel={true}
          isDarkMode={isDarkMode}
        />

        <div className="col-md-6">
          <label className="form-label">Zone</label>
          <Select
            value={selectedZone}
            onChange={(opt) => {
              setSelectedZone(opt);
              setForm((prev) => ({ ...prev, zoneId: opt?.value || null }));
            }}
            options={zoneOptions}
            styles={zoneSelectStyles}
            placeholder={form.serviceCity ? "Select zone" : "Select city first"}
            isDisabled={!form.serviceCity}
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="col-md-6">
          <FormInput
            label={isEdit ? "Shift date" : "From date"}
            type="date"
            name="shiftDateFrom"
            value={form.shiftDateFrom}
            onchange={handleChange}
          />
        </div>

        {!isEdit && (
          <div className="col-md-6">
            <FormInput
              label="To date"
              type="date"
              name="shiftDateTo"
              value={form.shiftDateTo}
              onchange={handleChange}
            />
            <small className="text-muted d-block mt-1">
              Creates one slot per day with the same times. Leave same as from date for a single day.
            </small>
          </div>
        )}

        <div className="col-md-6">
          <FormInput
            label="Start time"
            type="time"
            name="startTime"
            value={form.startTime}
            onchange={handleChange}
          />
        </div>

        <div className="col-md-6">
          <FormInput
            label="End time"
            type="time"
            name="endTime"
            value={form.endTime}
            onchange={handleChange}
          />
        </div>

        <div className="col-md-6">
          <FormInput
            label="Max riders"
            type="number"
            name="maxRiders"
            value={form.maxRiders}
            onchange={handleChange}
          />
        </div>

        <div className="col-md-6 d-flex align-items-end pb-1">
          <ToggleSwitch
            isChecked={form.isActive}
            onToggle={handleChange}
            label="Active"
            name="isActive"
          />
        </div>

        <div className="col-12">
          <label className="form-label">Notes</label>
          <textarea
            className="form-control"
            name="notes"
            rows={3}
            value={form.notes}
            onChange={handleChange}
            placeholder="Optional notes for this shift slot"
            style={fieldStyle}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default AddUpdateShiftSlotModal;
